/**
 * The sign-in rows (Option C, C2): Authentication, then the credentials the mode asks for (Login / User / Client ID and
 * Password / Client secret / Account key / SAS token), then the Entra tenant — the tenant always follows the
 * credentials (SQL Server: right after Authentication, since Entra asks none; Azure Data Explorer and Azure Storage:
 * after the client ID and secret). Each row renders only while its rule applies.
 */
import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { engineSupports } from '@/core/engines';
import type { ConnectionFormValues, DatabaseEngine } from '../../components/connectionFormSchema';
import { DEFAULT_LOGINS } from '../../components/connectionFieldDefaults';
import { AZURE_STORAGE_AUTH_OPTIONS } from '../../components/azureStorageFormRules';
import { fieldApplies } from '../state/connectionFieldRules';
import { ruleValidators } from '../state/fieldRuleValidators';
import { FormRow } from '../pane/FormRow';
import { FieldError, INVALID_CLASS, SELECT_CLASS, type ConnectionFormApi } from './fieldControls';
import { loginLabel, secretLabel } from './fieldLabels';

type Auth = ConnectionFormValues['authentication'];
type Props = { form: ConnectionFormApi; engine: DatabaseEngine; authentication: Auth };

function AuthenticationOptions({ engine }: { engine: DatabaseEngine }) {
  if (engine === 'azurestorage') {
    return <>{AZURE_STORAGE_AUTH_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</>;
  }
  if (engine === 'kusto') {
    return (
      <>
        <option value="Microsoft Entra ID (Interactive Browser)">Microsoft Entra ID (Interactive Browser)</option>
        <option value="Service Principal">Service principal (client ID and secret)</option>
        <option value="Azure CLI">Azure CLI (az login)</option>
      </>
    );
  }
  if (engineSupports(engine, 'connection', 'supportsWindowsAuth')) {
    return (
      <>
        <option value="SQL Server authentication">SQL Server authentication</option>
        <option value="Windows authentication">Windows authentication</option>
        <option value="Microsoft Entra ID (Interactive Browser)">Microsoft Entra ID (Interactive Browser)</option>
        <option value="Microsoft Entra ID (Device Code)">Microsoft Entra ID (Device Code)</option>
      </>
    );
  }
  return (
    <>
      <option value="Password">Password</option>
      <option value="No password">No password (trust)</option>
    </>
  );
}

/** A password-style input with its show / hide toggle (the icons cross-fade, so the toggle can reverse mid-way). */
function SecretInput({ field, label }: { field: any; label: string }) {
  const [shown, setShown] = useState(false);
  const icon = (visible: boolean) => cn('absolute inset-0 h-4 w-4 transition-[opacity,scale,filter] duration-300 ease-[cubic-bezier(0.2,0,0,1)]', visible ? 'scale-100 opacity-100 blur-[0px]' : 'scale-[0.25] opacity-0 blur-[4px]');
  return (
    <div className="relative">
      <Input
        id="password"
        name="password"
        type={shown ? 'text' : 'password'}
        className="pr-10"
        value={field.state.value ?? ''}
        onChange={(event) => field.handleChange(event.target.value)}
        onBlur={field.handleBlur}
        data-testid="password-input"
      />
      <button
        type="button"
        className="absolute inset-y-0 right-0 flex items-center pl-2 pr-3 text-muted-foreground hover:text-foreground"
        onClick={() => setShown(!shown)}
        tabIndex={-1}
        aria-label={shown ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
        data-testid="password-toggle"
      >
        <span className="relative h-4 w-4">
          <Eye className={icon(!shown)} />
          <EyeOff className={icon(shown)} />
        </span>
      </button>
    </div>
  );
}

function TenantRow({ form, engine, authentication }: Props) {
  const required = (engine === 'kusto' || engine === 'azurestorage') && authentication === 'Service Principal';
  return (
    <form.Field name="entraTenantId" validators={ruleValidators('entraTenantId', ['onChange', 'onBlur'])}>
      {(field: any) => (
        <FormRow
          row="entraTenantId"
          label="Tenant ID"
          htmlFor="entraTenantId"
          note={required ? undefined : 'optional'}
          hint={required ? 'Required for a service principal.' : <>Empty: <span className="font-mono">common</span>. Set it when you belong to several tenants or sign in with a guest or personal Microsoft account.</>}
        >
          <Input
            id="entraTenantId"
            name="entraTenantId"
            placeholder="Directory ID (GUID) or domain, e.g. contoso.com"
            className={field.state.meta.errors.length ? INVALID_CLASS : undefined}
            value={field.state.value ?? ''}
            onChange={(event) => field.handleChange(event.target.value)}
            onBlur={field.handleBlur}
            data-testid="entra-tenant-id-input"
          />
          <FieldError meta={field.state.meta} />
        </FormRow>
      )}
    </form.Field>
  );
}

export function SignInRows({ form, engine, authentication }: Props) {
  const values = { engine, authentication };
  const secret = secretLabel(engine, authentication);
  return (
    <>
      {fieldApplies('authentication', values) && (
        <form.Field name="authentication" validators={ruleValidators('authentication', ['onChange'])}>
          {(field: any) => (
            <FormRow row="authentication" label="Authentication" htmlFor="authentication">
              <select
                id="authentication"
                name="authentication"
                className={SELECT_CLASS}
                value={field.state.value}
                onChange={(event) => field.handleChange(event.target.value as Auth)}
                onBlur={field.handleBlur}
                data-testid="authentication-select"
              >
                <AuthenticationOptions engine={engine} />
              </select>
            </FormRow>
          )}
        </form.Field>
      )}
      {fieldApplies('login', values) && (
        <form.Field name="login">
          {(field: any) => (
            <FormRow row="login" label={loginLabel(engine)} htmlFor="login">
              <Input
                id="login"
                name="login"
                placeholder={DEFAULT_LOGINS[engine] || undefined}
                value={field.state.value ?? ''}
                onChange={(event) => field.handleChange(event.target.value)}
                onBlur={() => {
                  if (!field.state.value?.trim() && DEFAULT_LOGINS[engine]) field.handleChange(DEFAULT_LOGINS[engine]);
                  field.handleBlur();
                }}
                data-testid="login-input"
              />
            </FormRow>
          )}
        </form.Field>
      )}
      {fieldApplies('password', values) && (
        <form.Field name="password">
          {(field: any) => (
            <FormRow row="password" label={secret} htmlFor="password">
              <SecretInput field={field} label={secret} />
            </FormRow>
          )}
        </form.Field>
      )}
      {fieldApplies('entraTenantId', values) && <TenantRow form={form} engine={engine} authentication={authentication} />}
    </>
  );
}
